import { api } from '../client/axiosClient';
import { CustomResponseEntity } from '../types/dtos/common';
import { InspectionsFindingsItemDto } from '../types/dtos/inspection';
import { getApiUrl } from '../config/apiConfig';

/**
 * InspectionFindingItemService handles CRUD operations for inspection finding items.
 *
 * URL Pattern: /api/v1/inspections/{inspectionId}/findings/{findingId}/items
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * @deprecated organizationId method parameters are deprecated and no longer used.
 */
class InspectionFindingItemService {
  /**
   * Build the base path for finding items
   * @param _organizationId - Deprecated: Organization ID (no longer used)
   * @param inspectionId - The inspection ID
   * @param findingId - The finding ID
   */
  private getBasePath(_organizationId: string, inspectionId: string, findingId: string): string {
    return getApiUrl(`/inspections/${inspectionId}/findings/${findingId}/items`);
  }

  /**
   * Build the path for a specific finding item
   * @param _organizationId - Deprecated: Organization ID (no longer used)
   * @param inspectionId - The inspection ID
   * @param findingId - The finding ID
   * @param id - The item ID
   */
  private getByIdPath(_organizationId: string, inspectionId: string, findingId: string, id: string): string {
    return getApiUrl(`/inspections/${inspectionId}/findings/${findingId}/items/${id}`);
  }

  async findAll(
    _organizationId: string,
    inspectionId: string,
    findingId: string,
    isActive?: boolean
  ): Promise<CustomResponseEntity<InspectionsFindingsItemDto[]>> {
    return api.get(this.getBasePath(_organizationId, inspectionId, findingId), {
      params: { isActive },
    });
  }

  async findById(
    _organizationId: string,
    inspectionId: string,
    findingId: string,
    id: string
  ): Promise<CustomResponseEntity<InspectionsFindingsItemDto>> {
    return api.get(
      this.getByIdPath(_organizationId, inspectionId, findingId, id)
    );
  }

  async create(
    _organizationId: string,
    inspectionId: string,
    findingId: string,
    data: InspectionsFindingsItemDto
  ): Promise<CustomResponseEntity<InspectionsFindingsItemDto>> {
    return api.post(
      this.getBasePath(_organizationId, inspectionId, findingId),
      data
    );
  }

  async update(
    _organizationId: string,
    inspectionId: string,
    findingId: string,
    id: string,
    data: InspectionsFindingsItemDto
  ): Promise<CustomResponseEntity<InspectionsFindingsItemDto>> {
    return api.put(
      this.getByIdPath(_organizationId, inspectionId, findingId, id),
      data
    );
  }

  async delete(
    _organizationId: string,
    inspectionId: string,
    findingId: string,
    id: string
  ): Promise<CustomResponseEntity<void>> {
    return api.delete(
      this.getByIdPath(_organizationId, inspectionId, findingId, id)
    );
  }
}

export const inspectionFindingItemService = new InspectionFindingItemService(); 